import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import ApiClient from '../../config/ApiClient';
import { useRoleLabels } from '../../hooks/useRoleLabels';
import {
    AssignmentInvitation,
    AssignmentInvitationRole,
} from '../../object-actions/types/types';
import InviteUserForm from './InviteUserForm';
import PendingInvitationsList from './PendingInvitationsList';

interface InviteSupportModalProps {
    open: boolean;
    assignmentId: number;
    onClose: () => void;
    onInvited?: (invitation: AssignmentInvitation) => void;
    /**
     * Roles the user may pick. Defaults to coach + guardian (athlete onboarding).
     * Pass `['parent']` for coach onboarding guardian invites.
     */
    allowedRoles?: Array<Extract<AssignmentInvitationRole, 'coach' | 'parent'>>;
    title?: string;
    description?: string;
}

/**
 * Modal to invite a Coach and/or Guardian onto the assignment.
 */
const InviteSupportModal: React.FC<InviteSupportModalProps> = ({
    open,
    assignmentId,
    onClose,
    onInvited,
    allowedRoles = ['coach', 'parent'],
    title,
    description,
}) => {
    const { getRoleLabel } = useRoleLabels();
    const coachLabel = getRoleLabel('coach');
    const guardianLabel = getRoleLabel('parent');
    type SupportRole = Extract<AssignmentInvitationRole, 'coach' | 'parent'>;
    const roles: SupportRole[] = allowedRoles.length > 0 ? allowedRoles : ['coach', 'parent'];
    const defaultRole: SupportRole = roles[0] ?? 'coach';
    const [role, setRole] = useState<SupportRole>(defaultRole);
    const [pendingInvitations, setPendingInvitations] = useState<AssignmentInvitation[]>([]);
    const [loading, setLoading] = useState(false);

    const refreshInvitations = useCallback(async () => {
        if (!assignmentId) return;
        setLoading(true);
        try {
            const res = await ApiClient.get<AssignmentInvitation[]>(
                `/api/payment-assignments/${assignmentId}/invitations/`,
            );
            if (res.success && Array.isArray(res.data)) {
                setPendingInvitations(res.data);
            }
        } finally {
            setLoading(false);
        }
    }, [assignmentId]);

    useEffect(() => {
        if (!open) return;
        setRole(defaultRole);
        refreshInvitations();
    }, [open, defaultRole, refreshInvitations]);

    const handleInvited = (invitation: AssignmentInvitation) => {
        setPendingInvitations((prev) => [invitation, ...prev]);
        onInvited?.(invitation);
    };

    const roleLabel = role === 'coach' ? coachLabel : guardianLabel;
    const supportPending = pendingInvitations.filter((inv) => roles.includes(inv.role as 'coach' | 'parent'));
    const showRolePicker = roles.length > 1;
    const dialogTitle =
        title ??
        (showRolePicker
            ? `Invite a ${coachLabel.toLowerCase()} or ${guardianLabel.toLowerCase()}`
            : `Invite a ${roleLabel.toLowerCase()}`);
    const dialogDescription =
        description ??
        (showRolePicker
            ? 'They must accept the invitation before you can take the assessment. Or close this and start with an AI coach instead.'
            : `Send an invitation so they can join this assignment.`);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="invite-support-title"
            data-testid="invite-support-modal"
        >
            <DialogTitle id="invite-support-title">
                {dialogTitle}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {dialogDescription}
                </Typography>
                {showRolePicker && (
                    <FormControl component="fieldset" sx={{ mb: 2 }}>
                        <RadioGroup
                            row
                            value={role}
                            onChange={(e) =>
                                setRole(e.target.value as 'coach' | 'parent')
                            }
                            data-testid="invite-support-role"
                        >
                            {roles.includes('coach') && (
                                <FormControlLabel
                                    value="coach"
                                    control={<Radio size="small" />}
                                    label={coachLabel}
                                />
                            )}
                            {roles.includes('parent') && (
                                <FormControlLabel
                                    value="parent"
                                    control={<Radio size="small" />}
                                    label={guardianLabel}
                                />
                            )}
                        </RadioGroup>
                    </FormControl>
                )}
                <Alert severity="info" sx={{ mb: 2 }}>
                    Inviting a {roleLabel.toLowerCase()}. They will get a link to join.
                </Alert>
                <InviteUserForm
                    assignmentId={assignmentId}
                    role={role}
                    onInvited={handleInvited}
                />
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={18} />
                    </Box>
                ) : (
                    <Box sx={{ mt: 2 }}>
                        <PendingInvitationsList
                            assignmentId={assignmentId}
                            invitations={supportPending}
                            onChanged={refreshInvitations}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} data-attr="invite-support-modal_done">
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InviteSupportModal;
